/**
 * useCrmWorkspaceCategories - Hook para buscar categorias de workspace CRM
 * 
 * Busca categorias globais do sistema (sem tenant_id).
 * Categorias são gerenciadas via migrations pela equipe de desenvolvimento.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para rastreamento
const logger = createLogger('useCrmWorkspaceCategories');

/**
 * Interface da Categoria de Workspace
 */
export interface CrmWorkspaceCategory {
  id: string;
  slug: string;
  name: string;
  icon: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

/**
 * Hook para buscar categorias de workspace CRM
 * Retorna apenas categorias ativas ordenadas por sort_order
 * 
 * @returns Objeto com dados, estado de loading e erro
 */
export function useCrmWorkspaceCategories() {
  return useQuery({
    queryKey: ['crm-workspace-categories'],
    queryFn: async (): Promise<CrmWorkspaceCategory[]> => {
      logger.info('Buscando categorias de workspace');

      const { data, error } = await supabase
        .from('crm_workspace_categories')
        .select('id, slug, name, icon, description, sort_order, is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        logger.error('Erro ao buscar categorias', error);
        throw error;
      }

      logger.info('Categorias carregadas com sucesso', { count: data?.length || 0 });

      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutos de cache (categorias mudam raramente)
  });
}

/**
 * Hook para buscar uma categoria específica por ID
 * 
 * @param categoryId - ID da categoria
 * @returns Objeto com dados da categoria
 */
export function useCrmWorkspaceCategory(categoryId: string | null) {
  return useQuery({
    queryKey: ['crm-workspace-category', categoryId],
    queryFn: async (): Promise<CrmWorkspaceCategory | null> => {
      if (!categoryId) return null;

      const { data, error } = await supabase
        .from('crm_workspace_categories')
        .select('id, slug, name, icon, description, sort_order, is_active')
        .eq('id', categoryId)
        .single();

      if (error) {
        logger.error('Erro ao buscar categoria', error);
        throw error;
      }

      return data;
    },
    enabled: !!categoryId,
    staleTime: 30 * 60 * 1000,
  });
}
